import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Helmet } from 'react-helmet-async';
import ReactPlayer from 'react-player';

import useScreen from 'hooks/useScreen';
import ReactVimeo from 'components/consumer/ReactVimeo';
import RatioContainer from 'components/consumer/RatioContainer';
import BrowserOnly from 'components/consumer/BrowserOnly';

const PDPProductIntroVideoWistia = ({ videoId, variant }) => {
  const [isThumbnailImageLoaded, setIsThumbnailImageLoaded] = useState(false);

  return (
    !!videoId && (
      <div
        // We use a custom className here to support overriding nested third
        // party styles in globals-tailwind.css
        className={classNames('pdp-wistia-video h-full flex items-center', {
          'w-full': variant === 'PDPDesktopFullScreenGallery2',
        })}
      >
        <Helmet>
          <script
            src={`//fast.wistia.com/embed/medias/${videoId}.jsonp`}
            async
          />

          <script src="//fast.wistia.com/assets/external/E-v1.js" async />
        </Helmet>

        <div className="wistia_responsive_padding">
          <div className="wistia_responsive_wrapper">
            <div
              className={`wistia_embed wistia_async_${videoId} videoFoam=true muted=true autoPlay={true} playbar=false qualityControl=false settingsControl=false smallPlayButton=false fullscreenButton=false silentAutoPlay=allow googleAnalytics=false endVideoBehavior=reset`}
            >
              <div
                className={classNames('wistia_swatch', {
                  wistia_swatch_img_loaded: !!isThumbnailImageLoaded,
                })}
              >
                <img
                  src={`//fast.wistia.com/embed/medias/${videoId}/swatch`}
                  alt=""
                  aria-hidden="true"
                  onLoad={() => {
                    setIsThumbnailImageLoaded(true);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  );
};

const PDPProductIntroVideoVimeo = ({ videoId, variant }) => {
  // If video URL was copied while logged into Vimeo, it may be passed into the
  // component in the format https://vimeo.com/manage/videos/560538232 but the
  // only format supported by the Vimeo player is https://vimeo.com/560538232
  const vimeoVideoId = videoId
    ? videoId.replace('/manage/videos', '')
    : videoId;

  return (
    !!videoId && (
      <div
        className={classNames('h-full flex items-center justify-center', {
          'w-full': variant === 'PDPDesktopFullScreenGallery2',
        })}
      >
        <RatioContainer ratio={9 / 16} wrapperClassName="flex-1">
          <ReactVimeo
            className="w-full h-full relative"
            video={vimeoVideoId}
            responsive
            autoplay
            showTitle={false}
          />
        </RatioContainer>
      </div>
    )
  );
};

const PDPProductIntroVideoPlayer = ({ videoId, variant }) => {
  const { isDesktop } = useScreen();
  const [isReady, setIsReady] = useState(false);

  return (
    !!videoId && (
      <div
        className={classNames('h-full flex items-center justify-center', {
          'w-full': variant === 'PDPDesktopFullScreenGallery2',
        })}
      >
        <RatioContainer ratio={9 / 16} wrapperClassName="flex-1">
          <div className="w-full h-full relative">
            <ReactPlayer
              url={videoId}
              {...(isDesktop && isReady && { playing: true })}
              loop
              controls
              volume={1}
              muted
              width="100%"
              height="100%"
              onReady={() => setIsReady(true)}
              onError={error => {
                throw new Error(
                  `ReactPlayer error: ${error.message || 'Unknown error'}`
                );
              }}
            />
          </div>
        </RatioContainer>
      </div>
    )
  );
};

const PDPProductIntroVideo = ({ videoId, variant }) => {
  if (!videoId) {
    return null;
  }

  return (
    <BrowserOnly>
      {videoId.startsWith('http') ? (
        <PDPProductIntroVideoPlayer videoId={videoId} variant={variant} />
      ) : !!videoId && /[a-z]/g.test(videoId) === true ? (
        <PDPProductIntroVideoWistia videoId={videoId} variant={variant} />
      ) : (
        <PDPProductIntroVideoVimeo videoId={videoId} variant={variant} />
      )}
    </BrowserOnly>
  );
};

PDPProductIntroVideoWistia.propTypes = {
  variant: PropTypes.oneOf(['PDPDesktopFullScreenGallery2']),
  videoId: PropTypes.string,
};

PDPProductIntroVideoVimeo.propTypes = {
  variant: PropTypes.oneOf(['PDPDesktopFullScreenGallery2']),
  videoId: PropTypes.string,
};

PDPProductIntroVideoPlayer.propTypes = {
  variant: PropTypes.oneOf(['PDPDesktopFullScreenGallery2']),
  videoId: PropTypes.string,
};

PDPProductIntroVideo.propTypes = {
  variant: PropTypes.oneOf(['PDPDesktopFullScreenGallery2']),
  videoId: PropTypes.string,
};

export default PDPProductIntroVideo;
